/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, MouseEvent } from 'react';
import type { ReactNode } from 'react';
import debounce from 'lodash/debounce';

import { FullStoryAPI } from '@zapier/react-fullstory';
import { formatTimeSpan, formatDateTime } from '@zapier/date';
import { Icon, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  emitUserInteractionClick,
  emitUserInteractionComponentInView,
  emitUserInteractionHover,
  InteractionGoal,
  InteractionName,
} from '../../../../utils/avroEvents';

import { useUniversalNavContext } from '../../../../hooks';
import { TrialPlanTypes } from '../../../../constants';

import UsageBar from './UsageBar';

export type Props = {
  /**
   * Content to render at bottom of section. Typically used to
   * render a CTA to upgrade plan or similar.
   */
  children?: ReactNode;
  /**
   * Whether or not the type of plan is metered
   */
  isPlanMetered?: boolean;
  /**
   * Location where this component is rendered. Used for Avro tracking.
   */
  systemName?: string;
  /**
   * Text to display for the manage plan link.
   */
  managePlanLabel?: string;
  /**
   * URL to where the plan can be managed.
   */
  managePlanHref?: string;
  /**
   * Maximum number of free tasks for the usage period.
   */
  numFreeTasksMax?: number;
  /**
   * Number of tasks that have been used in the usage period.
   */
  numTasks: number;
  /**
   * Maximum number of tasks that can be used in the usage period.
   */
  numTasksMax: number;
  /**
   * Number of Zaps that are currently on.
   */
  numZaps: number;
  /**
   * Maximum number of Zaps that can be on.
   */
  numZapsMax: number;
  /**
   * String representation of when usage resets.
   */
  resetDate: string;
  /**
   * The title of the section within the sidebar.
   */
  title?: string;
  /**
   * Name of the trial plan the account is on, if it exists
   */
  trialPlanName?: TrialPlanTypes;
  /**
   * End date for the account's trial plan, if it exists
   */
  trialPlanEndDate?: string;
};

const Styles = labelStyles('UniversalSidebarUsage', {
  root: {
    display: 'grid',
    gap: 'var(--zds-space-12, 12px)',
    whiteSpace: 'nowrap',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 'var(--zds-space-12, 12px)',
    alignItems: 'center',
    font: Typography.base.fontFamily,
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--zds-text-default, #413735)',
    '& svg': {
      color: 'inherit',
    },
  },
  planName: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    font: `var(--zds-body-2, 500 14px/24px ${Typography.base.fontFamily})`,
  },
  usages: {
    display: 'grid',
    gap: 'var(--zds-space-8, 8px)',
    marginBottom: 'var(--zds-space-8, 8px)',
  },
  resetInfo: {
    display: 'grid',
    gap: 'var(--zds-space-8, 8px)',
    font: `var(--zds-minimal-print-1, 400 12px/20px ${Typography.base.fontFamily})`,
    gridTemplateRows: '1fr auto',
    paddingTop: '10px',

    color: 'var(--zds-text-weakest, #6F6765)',
    '> a': {
      color: 'var(--zds-text-link, #695BE8) !important',
      width: 'max-content',
      '&:hover': {
        color: 'var(--zds-text-link-hover, #503ebd) !important',
      },
      '&:focus-visible': {
        outlineColor: 'var(--zds-text-link, #695BE8) !important',
      },
      '&:focus': {
        outlineColor: 'none !important',
      },
    },
  },
});

type UsagesProps = Required<
  Pick<
    Props,
    | 'isPlanMetered'
    | 'numFreeTasksMax'
    | 'numTasks'
    | 'numTasksMax'
    | 'numZaps'
    | 'numZapsMax'
  >
>;

const Usages = (props: UsagesProps) => {
  const hasUsedExtraTasks =
    props.isPlanMetered && props.numTasks > props.numFreeTasksMax;
  return (
    <div css={Styles.usages} data-zds>
      <UsageBar
        label={props.isPlanMetered ? 'Included Tasks' : 'Tasks'}
        max={props.isPlanMetered ? props.numFreeTasksMax : props.numTasksMax}
        value={
          props.isPlanMetered
            ? Math.min(props.numTasks, props.numFreeTasksMax)
            : props.numTasks
        }
      />
      {hasUsedExtraTasks && (
        <UsageBar
          label="Additional Tasks"
          max={props.numTasksMax - props.numFreeTasksMax}
          value={props.numTasks - props.numFreeTasksMax}
        />
      )}
      <UsageBar label="Zaps" max={props.numZapsMax} value={props.numZaps} />
    </div>
  );
};

/**
 * Renders account usage data within the `InAppSidebar`.
 *
 * ## For change and support requests:
 *
 * - Contact __#team-manage__ for the logged in/in app experience.
 * - Contact __#team-content-platforms__ for the logged out/out of app experience.
 */
const PlanUsage = ({
  numTasks,
  numTasksMax,
  numZaps,
  numZapsMax,
  resetDate,
  systemName = 'design_system',
  children = undefined,
  trialPlanName = undefined,
  trialPlanEndDate = undefined,
  isPlanMetered = false,
  numFreeTasksMax = 0,
  managePlanHref = '/app/settings/billing',
  managePlanLabel = 'Manage plan',
  title = 'Usage',
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const managePlanURL = getFullyQualifiedURL(managePlanHref);
  const relativeDate = formatTimeSpan(Date.now(), resetDate, false);

  useEffect(() => {
    emitUserInteractionComponentInView(
      InteractionName.plan_usage_component_in_view,
      InteractionGoal.plan_usage,
      systemName
    );
    FullStoryAPI('event', `Plan usage component in view from ${systemName}`);
  }, []);

  const handleEmitAvroClickEvent = (event: MouseEvent) => {
    event.preventDefault();
    FullStoryAPI('event', `Plan usage click from ${systemName}`);
    emitUserInteractionClick(
      InteractionName.plan_usage_click,
      InteractionGoal.plan_usage,
      managePlanLabel,
      managePlanHref,
      systemName
    ).finally(() => (location.href = managePlanURL || ''));
  };

  const handleEmitHover = () => {
    emitUserInteractionHover(
      InteractionName.plan_usage_mouse_enter,
      InteractionGoal.plan_usage,
      systemName
    );
    FullStoryAPI('event', `Plan usage hover from ${systemName}`);
  };

  const handleDelayEmitHover = debounce(handleEmitHover, 1000);

  return (
    <section onMouseEnter={handleDelayEmitHover} css={Styles.root} data-zds>
      <div css={Styles.header} data-zds>
        <Icon name="miscWallet" size={20} />
        <h3 css={Styles.planName} data-zds>
          {trialPlanName ? `${trialPlanName} plan (Trial)` : title}
        </h3>
      </div>
      <div data-zds>
        <Usages
          {...{
            isPlanMetered,
            numFreeTasksMax,
            numTasks,
            numTasksMax,
            numZaps,
            numZapsMax,
          }}
        />

        <p css={Styles.resetInfo} data-zds>
          <span data-zds>
            {trialPlanName && trialPlanEndDate
              ? `Trial ends on ${formatDateTime(trialPlanEndDate, 'default')}`
              : `Usage resets in ${relativeDate}`}
          </span>
          <Link onClick={handleEmitAvroClickEvent} href={managePlanURL}>
            {managePlanLabel}
          </Link>
        </p>
      </div>
      {children}
    </section>
  );
};

export default PlanUsage;
